import React from 'react';
import { AlertCircle, Search, X } from 'lucide-react';
import { FilterSearchInput } from '../../../components/FormComponents';
import {
  actionLabel,
  groupActions,
  ENTITY_TYPE_OPTIONS,
  DATE_RANGE_OPTIONS,
} from './activityMeta';

export default function ActivityFilters({
  q, setQ,
  userId, setUserId,
  action, setAction,
  entityType, setEntityType,
  range, setRange,
  customFrom, setCustomFrom,
  customTo, setCustomTo,
  errorsOnly, setErrorsOnly,
  actions, actors,
  hasFilters, clearFilters,
}) {
  const actionGroups = groupActions(actions);

  const actorLabel = (a) => a.userName || a.userEmail || a.userId;
  const rangeLabel = DATE_RANGE_OPTIONS.find((o) => o.value === range)?.label;
  const entityLabel = ENTITY_TYPE_OPTIONS.find((o) => o.value === entityType)?.label;
  const userLabel = actors.find((a) => a.userId === userId);

  // Active-filter chips — what's applied, each individually removable.
  const chips = [
    q && { key: 'q', label: `“${q}”`, onRemove: () => setQ('') },
    userId && { key: 'user', label: userLabel ? actorLabel(userLabel) : 'User', onRemove: () => setUserId('') },
    action && { key: 'action', label: actionLabel(action), onRemove: () => setAction('') },
    entityType && { key: 'entity', label: entityLabel, onRemove: () => setEntityType('') },
    range && { key: 'range', label: rangeLabel, onRemove: () => setRange('') },
    errorsOnly && { key: 'errors', label: 'Errors only', onRemove: () => setErrorsOnly(false) },
  ].filter(Boolean);

  return (
    <>
      {/* Search on its own row, like every other list page: .filter-row is a
          GRID of minmax(150px, 1fr) tracks, and a search box with a larger
          min-width overflows its track and overlaps the neighboring select at
          in-between viewport widths. The shared FilterSearchInput also carries
          data-search-input so the bare-`/` shortcut focuses it (doc-356). */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <FilterSearchInput
            data-search-input
            className="filter-search-wide"
            icon={Search}
            placeholder="Search user, target, or action…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        {/* Errors only — failed requests and failed background jobs (the rows
            recording what a user SAW go wrong, with the message they saw). */}
        <button
          type="button"
          className={errorsOnly ? 'btn sm toggled' : 'btn outline sm'}
          onClick={() => setErrorsOnly(!errorsOnly)}
          style={{ whiteSpace: 'nowrap' }}
          title="Show only failed actions — the error each user actually hit"
        >
          <AlertCircle className="w-3.5 h-3.5" />
          Errors
        </button>
      </div>

      <div className="filter-row" style={{ marginBottom: chips.length ? 10 : 16 }}>
        <select className="filter-select" value={userId} onChange={(e) => setUserId(e.target.value)}>
          <option value="">All users</option>
          {actors.map((a) => (
            <option key={a.userId} value={a.userId}>{actorLabel(a)}</option>
          ))}
        </select>

        <select className="filter-select" value={action} onChange={(e) => setAction(e.target.value)}>
          <option value="">All actions</option>
          {actionGroups.map((g) => (
            <optgroup key={g.key} label={g.label}>
              {g.actions.map((a) => (
                <option key={a} value={a}>{actionLabel(a)}</option>
              ))}
            </optgroup>
          ))}
        </select>

        {/* Option keys are prefix + value + index (doc-339): both lists lead
            with an empty-valued placeholder ("All targets" / "Any time"), so
            keying on the value alone means a second empty-valued entry collides
            with it and React silently drops one. */}
        <select className="filter-select" value={entityType} onChange={(e) => setEntityType(e.target.value)}>
          {ENTITY_TYPE_OPTIONS.map((o, i) => (
            <option key={`targets-${o.value || ''}-${i}`} value={o.value}>{o.label}</option>
          ))}
        </select>

        <select className="filter-select" value={range} onChange={(e) => setRange(e.target.value)}>
          {DATE_RANGE_OPTIONS.map((o, i) => (
            <option key={`range-${o.value || ''}-${i}`} value={o.value}>{o.label}</option>
          ))}
        </select>

        {range === 'custom' && (
          <>
            <input
              type="date"
              className="input"
              value={customFrom}
              max={customTo || undefined}
              onChange={(e) => setCustomFrom(e.target.value)}
              title="From date"
            />
            <input
              type="date"
              className="input"
              value={customTo}
              min={customFrom || undefined}
              onChange={(e) => setCustomTo(e.target.value)}
              title="To date"
            />
          </>
        )}
      </div>

      {chips.length > 0 && (
        <div className="active-filters" style={{ marginBottom: 16 }}>
          {chips.map((c) => (
            <span key={c.key} className="active-filter-chip">
              {c.label}
              <button type="button" onClick={c.onRemove} aria-label={`Remove ${c.label}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          {hasFilters && (
            <button className="btn ghost xs" onClick={clearFilters}>
              <X className="w-3 h-3" />
              Clear all
            </button>
          )}
        </div>
      )}
    </>
  );
}
